import { inject, Injectable } from '@angular/core';
import { Meta, Title, type MetaDefinition } from '@angular/platform-browser';
import { WindowService } from '@big-direkt/utils/environment';
import { type ImageAuxData } from '../models/image-aux-data';
import { type SocialMetaTag } from '../models/social-meta-tag';
import { type SocialShareData } from '../models/social-share-data';

@Injectable({
    providedIn: 'root',
})
export class SeoSocialShareService {
    private readonly metaService = inject(Meta);
    private readonly titleService = inject(Title);
    private readonly window = inject(WindowService);

    public setData(data: SocialShareData): void {
        this.setSection(data.section);
        this.setKeywords(data.keywords);
        this.setTitle(data.title);
        this.setType(data.type);
        this.setDescription(data.description);
        this.setImage(data.image, data.imageAuxData);
        this.setUrl(data.url);
        this.setPublished(data.published);
        this.setModified(data.modified);
        this.setAuthor(data.author);
    }

    public setTitle(content = ''): void {
        this.titleService.setTitle(content);

        this.setMetaTags([
            { name: 'twitter:title', content },
            { name: 'twitter:image:alt', content },
            { property: 'og:image:alt', content },
            { property: 'og:title', content },
            { name: 'title', content },
            { itemprop: 'name', content, selector: "itemprop='name'" },
        ]);
    }

    public setDescription(content?: string): void {
        this.setMetaTags([
            { name: 'twitter:description', content },
            { property: 'og:description', content },
            { name: 'description', content },
            { itemprop: 'description', content, selector: "itemprop='description'" },
        ]);
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    public setImage(content?: string, auxData?: ImageAuxData): void {
        this.setMetaTags([
            { name: 'twitter:image', content },
            { itemprop: 'image', content, selector: "itemprop='image'" },
            { property: 'og:image', content },
            { property: 'og:image:height', content: auxData?.height?.toString() },
            { property: 'og:image:width', content: auxData?.width?.toString() },
            { property: 'og:image:alt', content: auxData?.alt },
            { property: 'twitter:image:alt', content: auxData?.alt },
            { property: 'og:image:type', content: auxData?.mimeType },
            { property: 'og:image:secure_url', content: auxData?.secureUrl },
        ]);
    }

    public setUrl(content?: string): void {
        this.setMetaTagWithProperty('og:url', content);
        this.setCanonicalUrl(content);
    }

    public setMetaTag(tag: SocialMetaTag): void {
        if (tag.content) {
            const { selector } = tag;

            if (!selector) {
                this.metaService.updateTag(tag as MetaDefinition);

                return;
            }

            delete tag.selector;

            this.metaService.updateTag(tag as MetaDefinition, selector);

            return;
        }

        const attrSelector = tag.name
            ? `name='${tag.name}'`
            // eslint-disable-next-line sonarjs/no-nested-conditional
            : tag.property
                ? `property='${tag.property}'`
                // eslint-disable-next-line sonarjs/no-nested-conditional
                : tag.itemprop
                    ? `itemprop='${tag.itemprop}'`
                    : undefined;

        if (attrSelector) {
            this.metaService.removeTag(attrSelector);
        }
    }

    public setLanguageAlternativeUrl(hreflang: string, href?: string): void {
        this.updateHeaderChild(`link[rel='alternate'][hreflang='${hreflang}']`, {
            rel: 'alternate',
            hreflang,
            href,
        });
    }

    public setCanonicalUrl(href?: string): void {
        this.updateHeaderChild("link[rel='canonical']", {
            rel: 'canonical',
            href,
        });
    }

    private setKeywords(content?: string): void {
        this.setMetaTagWithName('keywords', content);
    }

    private setSection(content?: string): void {
        this.setMetaTagWithName('article:section', content);
    }

    private setType(content?: string): void {
        this.setMetaTagWithProperty('og:type', content);
    }

    private setPublished(publishedDateString?: string): void {
        const content = publishedDateString ? new Date(publishedDateString).toISOString() : undefined;

        this.setMetaTags([
            { name: 'article:published_time', content },
            { name: 'published_date', content },
        ]);
    }

    private setModified(modifiedDateString?: string): void {
        const content = modifiedDateString ? new Date(modifiedDateString).toISOString() : undefined;

        this.setMetaTags([
            { name: 'article:modified_time', content },
            { name: 'og:updated_time', content },
        ]);
    }

    private setAuthor(content?: string): void {
        this.setMetaTags([
            { name: 'article:author', content },
            { name: 'author', content },
        ]);
    }
    private setMetaTagWithName(name: string, content?: string): void {
        this.setMetaTag({ name, content });
    }

    private setMetaTagWithProperty(property: string, content?: string): void {
        this.setMetaTag({ property, content });
    }

    private setMetaTags(metaTags: SocialMetaTag[]): void {
        for (const metaTag of metaTags) {
            this.setMetaTag(metaTag);
        }
    }

    private updateHeaderChild(selector: string, attributes: Record<string, string | undefined>): void {
        const document = this.window.document();

        // first remove potential previous url
        const canonicalElement = document.head.querySelector(selector);

        if (canonicalElement) {
            document.head.removeChild(canonicalElement);
        }

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (attributes['href']) {
            const link: HTMLLinkElement = document.createElement('link');

            for (const item of Object.entries(attributes)) {
                link.setAttribute(item[0], String(item[1]));
            }

            document.head.appendChild(link);
        }
    }
}
